// Cross-tab invalidation for the current read-only BusinessPricing authority.
//
// BusinessPricingService's qm-pricing-updated CustomEvent is intentionally
// same-document. This bridge fans that invalidation out without moving the
// rate book into browser storage. Focus/visibility also request a bounded
// strict refresh so a suspended tab cannot retain old commercial authority.

export const PRICING_AUTHORITY_CHANNEL_NAME = 'qm-pricing-authority-v1';
export const PRICING_AUTHORITY_STORAGE_KEY = 'qm_pricing_authority_revision';

export function subscribePricingAuthorityInvalidation(onInvalidate, {
    windowRef = typeof window !== 'undefined' ? window : null,
    documentRef = typeof document !== 'undefined' ? document : null,
    now = () => Date.now(),
    focusMinIntervalMs = 2000,
    debounceMs = 75,
} = {}) {
    if (!windowRef || typeof onInvalidate !== 'function') return () => {};

    let closed = false;
    let timer = null;
    let pendingReason = null;
    let lastFocusRefreshAt = 0;
    let channel = null;

    const request = (reason, { focusBounded = false } = {}) => {
        if (closed) return;
        const currentTime = now();
        if (focusBounded) {
            if (currentTime - lastFocusRefreshAt < focusMinIntervalMs) return;
            lastFocusRefreshAt = currentTime;
        }
        pendingReason = reason;
        if (timer != null) return;
        timer = windowRef.setTimeout(() => {
            timer = null;
            const nextReason = pendingReason;
            pendingReason = null;
            if (!closed) onInvalidate(nextReason);
        }, debounceMs);
    };

    const publish = () => {
        const message = { type: 'pricing-invalidated', at: now() };
        try { channel?.postMessage(message); } catch { /* storage fallback below */ }
        try { windowRef.localStorage?.setItem(PRICING_AUTHORITY_STORAGE_KEY, JSON.stringify(message)); } catch { /* sealed storage */ }
    };

    const onSameTabUpdated = () => {
        publish();
        request('same-tab-update');
    };
    const onStorage = (event) => {
        if (event?.key === PRICING_AUTHORITY_STORAGE_KEY) request('peer-storage-update');
    };
    const onFocus = () => request('focus', { focusBounded: true });
    const onVisibility = () => {
        if (!documentRef || documentRef.visibilityState === 'visible') {
            request('visibility', { focusBounded: true });
        }
    };

    try {
        if (typeof BroadcastChannel === 'function') {
            channel = new BroadcastChannel(PRICING_AUTHORITY_CHANNEL_NAME);
            channel.onmessage = (event) => {
                if (event?.data?.type === 'pricing-invalidated') request('peer-broadcast-update');
            };
        }
    } catch {
        channel = null;
    }

    windowRef.addEventListener('qm-pricing-updated', onSameTabUpdated);
    windowRef.addEventListener('storage', onStorage);
    windowRef.addEventListener('focus', onFocus);
    documentRef?.addEventListener?.('visibilitychange', onVisibility);

    return () => {
        closed = true;
        if (timer != null) windowRef.clearTimeout(timer);
        windowRef.removeEventListener('qm-pricing-updated', onSameTabUpdated);
        windowRef.removeEventListener('storage', onStorage);
        windowRef.removeEventListener('focus', onFocus);
        documentRef?.removeEventListener?.('visibilitychange', onVisibility);
        try { channel?.close(); } catch { /* best effort */ }
    };
}
